import React from "react";
import Header from "@components/header";
import InformCookiesPopup from "@components/inform_cookies_popup";
import { usePage } from "@inertiajs/react";
import "../entrypoints/i18n";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import rollbar from "@utils/rollbar.ts";
import ErrorBoundaryContext from "@contexts/error_boundary_context.ts";
import { queryClient, localStoragePersister } from "../queries/query_client";
import { useRailsFlash, type RailsFlash } from "@/sprout/flash";
import {
  type LocationControlSettings,
  LocationControlSettingsContext,
} from "@contexts/location_control_settings_context.ts";
import { EmotionCacheProvider } from "@/sprout/emotion_cache_provider.tsx";

interface PageProps {
  nonce: string;
  flash: RailsFlash;
  locationControlSettings: LocationControlSettings;
  [key: string]: unknown;
}

interface LayoutProps {
  publicFacing?: boolean;
  children: React.ReactNode;
  hideProfile?: boolean;
}

const ONE_DAY = 1000 * 60 * 60 * 24;

const Layout: React.FC<LayoutProps> = ({
  publicFacing = false,
  children,
  hideProfile = false,
}: LayoutProps) => {
  const { nonce, flash, locationControlSettings } = usePage<PageProps>().props;

  useRailsFlash(flash);

  return (
    <ErrorBoundaryContext.Provider value={rollbar}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: localStoragePersister,
          maxAge: ONE_DAY,
          buster: "v1",
        }}
      >
        <LocationControlSettingsContext.Provider value={locationControlSettings}>
          <EmotionCacheProvider>
            <div className="h-screen overflow-hidden flex flex-col">
              {!publicFacing && <Header hideProfile={hideProfile} />}
              <div className="h-full overflow-auto" data-provides="scroll-container">
                {children}
              </div>
            </div>
            <InformCookiesPopup />
            <ReactQueryDevtools initialIsOpen={false} styleNonce={nonce} />
          </EmotionCacheProvider>
        </LocationControlSettingsContext.Provider>
      </PersistQueryClientProvider>
    </ErrorBoundaryContext.Provider>
  );
};

export default Layout;
